import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import UploadIcon from '@mui/icons-material/Upload';
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  ClickAwayListener,
  FormControl,
  Grid,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from '@mui/material';
import { generateHTML } from '@tiptap/react';
import { Base64 } from 'js-base64';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import AxiosConfig from '../../../config/AxiosConfig.js';
import languages from '../../helpers/utils/languages.js';
import useTemplate from '../../hooks/template/useTemplate.js';
import LoadingButton from '../utils/common/LoadingButton.jsx';
import templateDefault from './defaultTemplate.js';
import { useAutoDeleteInlineAttachments } from './hooks/useAutoDeleteInlineAttachments.js';
import { useAutoSaveDraft } from './hooks/useAutoSaveDraft.js';
import { TemplateSelect } from './TemplateSelect.jsx';
import {
  extractInlineImagesFromEditor,
  HANDLE_MESSAGES_REQUEST,
} from './utils.js';

const TemplateDisplay = ({
  loadingSave,
  loadingSend,
  mutateTicket,
  mutate,
  setIsLoadingSend,
  setIsLoadingSave,
  ticket,
  draft,
  recipientType,
  subject,
  setSubject,
  extensions,
  editor,
  attachments,
  setAttachments,
  message,
  setMessage,
  receiverList,
  handleCancelNewMessage,
  handleSaveNewMessage,
  handleSendMessage,
  editorHasChanged,
  setEditorHasChanged,
  isInlineAttLoaded,
  setIsInlineAttLoaded,
  setIsEditorDirty,
  getAttachmentData,
  getAttachmentUrl,
  inlineAttachments,
  setInlineAttachments,
}) => {
  const profile = useSelector((state) => state?.login?.profile);
  const [open, setOpen] = useState(false);
  const [isLoadingTranslation, setIsLoadingTranslation] = useState(false);
  const anchorRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const { templateList, isLoading } = useTemplate();

  const templateListOptions = useMemo(() => {
    const filteredTemplateList = templateList?.filter(
      (template) => template?.isDraft === false
    );
    return filteredTemplateList || [];
  }, [templateList]);

  const [isTooLarge, setIsTooLarge] = useState(false);

  // Ref pour s'assurer que le template par défaut n'est appliqué qu'une seule fois
  const hasAutoDefaultAppliedRef = useRef(false);

  const handleMenuItemClick = (event, index) => {
    setSelectedIndex(index);
    setOpen(false);
  };

  const handleToggleLanguageList = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleCloseLanguageList = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleTemplateAdd = useCallback(
    async (value) => {
      if (!editor) {
        console.warn("[TemplateDisplay] L'éditeur n'est pas prêt.");
        return;
      }

      const templateId = value;
      const logoLink = 'pandora/logo-hoortrade.jpg';
      // Si l'utilisateur choisit "Sans Template"
      if (templateId === 'None') {
        hasAutoDefaultAppliedRef.current = true; // Empêche l'auto-application juste après
        if (editor.getHTML().length > 7) {
          setIsEditorDirty(true);
        }
        // suppression des pièces jointes inlines
        const inlineAttachmentsToDelete =
          attachments?.filter((att) => !att.isAtt) || [];
        if (inlineAttachmentsToDelete.length > 0) {
          for (const attachment of inlineAttachmentsToDelete) {
            await HANDLE_MESSAGES_REQUEST.deleteAttachment({
              draftId: draft._id,
              attachementToDelete: attachment,
              mutateTicket,
              mutate,
              setIsLoadingSend,
              setIsLoadingSave,
            });
          }
        }
        editor.commands.setContent('');
        setEditorHasChanged(true);
        setMessage(''); // Vider la prop message pour synchronisation
        return;
      }

      const selectedTemplateData = templateList.find(
        (template) => templateId === template?._id.toString()
      );

      const isDefaultTemplate =
        selectedTemplateData?.reference === 'DEFAULT-000';

      if (isDefaultTemplate) {
        let imgToInsertInEditor = null;

        // suppression des pièces jointes inlines
        const inlineAttachmentsToDelete =
          attachments?.filter((att) => !att.isAtt || att.link !== logoLink) ||
          [];
        if (inlineAttachmentsToDelete.length > 0) {
          for (const attachment of inlineAttachmentsToDelete) {
            await HANDLE_MESSAGES_REQUEST.deleteAttachment({
              draftId: draft._id,
              attachementToDelete: attachment,
              mutateTicket,
              mutate,
              setIsLoadingSend,
              setIsLoadingSave,
            });
          }
        }

        if (!ticket?.isFromMP) {
          let logoAttachmentInState = attachments.find(
            (att) => att?.link === logoLink
          );

          if (!logoAttachmentInState) {
            // Le logo n'est pas encore dans l'état des attachments
            const fetchedLogoData = await getAttachmentData({ link: logoLink });

            if (fetchedLogoData) {
              const tempImgData = {
                ...fetchedLogoData,
                name: 'logo-hoortrade.jpg',
                file: {
                  name: 'logo-hoortrade.jpg',
                  size: fetchedLogoData?.size,
                  type: fetchedLogoData?.mimeType || 'image/jpeg',
                },
                link: logoLink,
                isAtt: true,
                width: fetchedLogoData.width || 'auto',
                height: fetchedLogoData.height || 'auto',
              };

              const result = await HANDLE_MESSAGES_REQUEST.createAttachment({
                attachments,
                draftId: draft._id,
                attachmentData: tempImgData,
                mutateTicket,
                mutate,
                setIsLoadingSend,
                setIsLoadingSave,
              });

              if (!result?.error && result?.data?._id) {
                // Succès : l'attachement est créé, ajouter aux attachments
                const createdAttachmentData = result.data;
                const newAttachment = {
                  ...tempImgData,
                  _id: createdAttachmentData._id,
                  src: await getAttachmentUrl(createdAttachmentData._id),
                  status: 'default',
                };
                // IMPORTANT: Mettre à jour l'état `attachments` avec la pièce jointe confirmée par l'API
                setAttachments((prev) => [
                  ...prev.filter((att) => att?.link !== logoLink),
                  newAttachment,
                ]);
                imgToInsertInEditor = newAttachment; // Utiliser les données confirmées par l'API pour l'insertion
              } else if (result?.isDuplicateAttachment) {
                // Doublon : récupérer l'attachement existant
                logoAttachmentInState = attachments.find(
                  (att) => att?.link === logoLink
                );
                if (logoAttachmentInState && logoAttachmentInState?._id) {
                  const existingLogoUrl = await getAttachmentUrl(
                    logoAttachmentInState._id
                  );
                  imgToInsertInEditor = {
                    ...logoAttachmentInState,
                    src: existingLogoUrl,
                  };
                }
              } else {
                // Log l'erreur si la création échoue (result.error sera loggué si présent, sinon un message générique)
                console.error(
                  '[TemplateDisplay] Erreur lors de la création de la pièce jointe du logo:',
                  result?.error || "Aucune donnée valide retournée par l'API."
                );
              }
            } else {
              console.warn(
                '[TemplateDisplay] Données du logo par défaut introuvables.'
              );
            }
          } else {
            // Le logo est déjà dans l'état des attachments
            if (logoAttachmentInState?._id) {
              const existingLogoUrl = await getAttachmentUrl(
                logoAttachmentInState._id
              );
              imgToInsertInEditor = {
                ...logoAttachmentInState,
                src: existingLogoUrl,
              };
            } else {
              console.warn(
                "[TemplateDisplay] Logo existant sans ID, impossible de récupérer l'URL."
              );
            }
          }
        }

        // Insère l'image dans le template si elle a pu être préparée avec une source valide
        const templateContentJSON = templateDefault(imgToInsertInEditor);

        const htmlContent = generateHTML(templateContentJSON, extensions);
        editor.commands.setContent(htmlContent, false);

        HANDLE_MESSAGES_REQUEST.saveMessage({
          draft,
          profile: { _id: profile?._id },
          ticket,
          subject: subject,
          content: editor.getHTML(),
          receiverList: draft?.receivers || [],
          recipientType: draft?.recipientType,
          mutateTicket,
          mutate,
          setIsLoadingSave,
        });
        hasAutoDefaultAppliedRef.current = true;
        setEditorHasChanged(true);
        setIsEditorDirty(true);
      } else if (selectedTemplateData) {
        setIsLoadingSave(true);
        const response = await AxiosConfig.create(
          'post',
          `/templates/loadContent`
        )
          .call({
            ticketId: ticket?._id,
            templateId: selectedTemplateData?._id,
            draftId: draft?._id,
          })
          .getResponseData();

        if (response) {
          mutateTicket();

          if (editor && response?.htmlBody) {
            setSubject(response?.subject);
            const processedContent = Base64.decode(response?.htmlBody);
            editor.commands.setContent(processedContent, false);
            setMessage(processedContent);
          }
        }
        setIsLoadingSave(false);
      } else {
        console.warn(
          `[TemplateDisplay] Template avec l'ID ${templateId} non trouvé.`
        );
        hasAutoDefaultAppliedRef.current = true; // Une non-trouvaille est aussi une action gérée
      }
    },
    [
      editor,
      templateList,
      attachments,
      setAttachments,
      mutateTicket,
      mutate,
      setIsLoadingSend,
      setIsLoadingSave,
      draft,
      profile,
      ticket,
      subject,
      recipientType,
      receiverList,
      extensions,
      setIsEditorDirty,
      setEditorHasChanged,
      getAttachmentData,
      getAttachmentUrl,
      setMessage,
    ]
  );

  const translateText = async () => {
    await HANDLE_MESSAGES_REQUEST.translateText({
      editor,
      extensions,
      languages,
      selectedIndex,
      setIsLoadingTranslation,
    });
    setIsEditorDirty(true);
    setEditorHasChanged(true);
  };

  const handleBtnSendMessage = async () => {
    const messageToSend = editor.getHTML();
    setMessage(messageToSend);
    await handleSendMessage({ messageToSend });
  };

  const handleBtnSaveMessage = async () => {
    const messageToSave = editor.getHTML();
    setMessage(messageToSave);
    await handleSaveNewMessage(messageToSave);
  };

  const sendBtnDisabled = useMemo(() => {
    const editorIsEmpty = !editor || editor.getHTML().length <= 7;
    const noReceiver = receiverList?.length === 0;
    const attachementsNotUploaded = attachments.some((att) => !att?._id);
    const isLoading = loadingSave || loadingSend || isLoadingTranslation;
    const rabbitmqIsNotSet =
      ticket?.contactChannelType === 'RABBITMQ' && !ticket?.threadId;
    const noDraftToUpdate = !draft?._id;

    return (
      editorIsEmpty ||
      noReceiver ||
      attachementsNotUploaded ||
      isLoading ||
      rabbitmqIsNotSet ||
      noDraftToUpdate
    );
  }, [
    editor,
    receiverList,
    attachments,
    loadingSave,
    loadingSend,
    isLoadingTranslation,
    ticket,
    draft,
  ]);

  useAutoSaveDraft({
    draft,
    message,
    profile,
    ticket,
    subject,
    editor,
    receiverList,
    recipientType,
    attachments,
    setIsLoadingSave,
    mutateTicket,
    mutate,
    isLoading: loadingSave || loadingSend,
    editorHasChanged,
    setEditorHasChanged,
    setIsEditorDirty,
  });

  useAutoDeleteInlineAttachments({
    draft,
    message,
    ticket,
    subject,
    editor,
    attachments,
    inlineAttachments,
    isLoading,
    mutateTicket,
    mutate,
    editorHasChanged,
    setEditorHasChanged,
    setIsLoadingSave,
    setAttachments,
    setInlineAttachments,
    setIsInlineAttLoaded,
  });

  useEffect(() => {
    if (
      editor &&
      draft?._id &&
      (message === null || editor.getHTML().trim() === '<p></p>') &&
      !hasAutoDefaultAppliedRef.current &&
      !isLoading
    ) {
      handleTemplateAdd();
    }
  }, [editor, draft?._id, message, isLoading, handleTemplateAdd]);

  useEffect(() => {
    if (!draft?._id || recipientType === null) {
      hasAutoDefaultAppliedRef.current = false;
    }
  }, [draft?._id, recipientType]);

  useEffect(() => {
    if (selectedIndex !== null) return;
    if (ticket?.contact?.language) {
      const index = languages?.findIndex(
        (lang) => lang?.code2 === ticket?.contact?.language
      );
      if (index !== -1) {
        setSelectedIndex(index);
      }
    }
  }, [ticket, selectedIndex]);

  useEffect(() => {
    let totalSize = 0;
    attachments?.forEach((attachment) => {
      totalSize += attachment.file?.size;
    });

    setIsTooLarge(totalSize >= 20000000);
  }, [attachments]);

  useEffect(() => {
    if (isInlineAttLoaded) return;
    if (editor) {
      const newInlineAttachments = extractInlineImagesFromEditor({ editor });
      setInlineAttachments(newInlineAttachments);
    }
  }, [editor, isInlineAttLoaded]);

  if (!editor) {
    return null;
  }

  return (
    <Grid>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          marginTop: 1,
          alignItems: 'center',
        }}
      >
        <>
          <Typography component={'p'} sx={{ fontSize: '0.8em', color: 'info' }}>
            {draft?.updatedAt
              ? `Dernière sauvegarde le
        ${new Date(draft?.updatedAt).toLocaleString('fr-FR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        })}`
              : `Pas de brouillon sauvegardé`}
          </Typography>
          {(loadingSave || loadingSend) && (
            <CircularProgress color="info" size={16} />
          )}
        </>
      </Box>

      <Grid
        container
        direction="row"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1,
          marginTop: 1,
        }}
      >
        <FormControl sx={{ margin: 1, minWidth: '16rem' }}>
          <TemplateSelect
            onChange={handleTemplateAdd}
            templates={templateListOptions}
          />
        </FormControl>
        <ButtonGroup
          variant="outlined"
          ref={anchorRef}
          color="primary"
          aria-label="Langues"
          sx={{ height: '2.5rem' }}
        >
          <LoadingButton
            isLoading={isLoadingTranslation}
            onClick={translateText}
            disabled={selectedIndex === null}
          >
            {selectedIndex !== null ? (
              <>
                Traduire en {languages[selectedIndex]?.lang} <AutoFixHighIcon />
              </>
            ) : (
              'Pas de langue détectée'
            )}
          </LoadingButton>
          <Button
            size="small"
            aria-controls={open ? 'split-button-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-label="Selectionner une langue vers laquelle traduire"
            aria-haspopup="menu"
            onClick={handleToggleLanguageList}
          >
            <KeyboardArrowDownIcon />
          </Button>
        </ButtonGroup>
        <Popper
          sx={{
            zIndex: 1,
            height: '25rem',
            overflowY: 'scroll',
            boxShadow: '1px 1px 10px grey',
          }}
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          placement={'top'}
          disablePortal
        >
          <Paper>
            <ClickAwayListener onClickAway={handleCloseLanguageList}>
              <MenuList id="split-button-menu" autoFocusItem>
                {languages.map((lang, index) => (
                  <MenuItem
                    key={lang.code2}
                    selected={index === selectedIndex}
                    onClick={(event) => handleMenuItemClick(event, index)}
                  >
                    <span className={`fi fi-${lang?.flag}`} /> - {lang?.lang}
                  </MenuItem>
                ))}
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Popper>
        <Button
          size="small"
          variant="outlined"
          onClick={() =>
            window.open(
              'https://wetransfer.com/',
              '_blank',
              'noopener,noreferrer'
            )
          }
          color="info"
          sx={{ margin: 1, width: '8rem' }}
          endIcon={<UploadIcon />}
        >
          WeTransfer
        </Button>
      </Grid>
      <Grid
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Button
          size="small"
          variant="outlined"
          onClick={handleCancelNewMessage}
          color="warning"
          endIcon={<DeleteIcon />}
        >
          Annuler
        </Button>
        <Box sx={{ marginLeft: 1 }}>
          <Tooltip
            placement="top"
            title={
              receiverList?.length === 0 ? (
                'Veuillez ajouter un destinataire'
              ) : editor.getHTML().length <= 7 ? (
                'Veuillez écrire un message'
              ) : isTooLarge ? (
                <span style={{ fontSize: 20 }}>
                  Au dessus de la taille limite
                </span>
              ) : (
                ''
              )
            }
          >
            <span>
              <LoadingButton
                isLoading={loadingSave}
                size="small"
                variant="outlined"
                onClick={handleBtnSaveMessage}
                color="primary"
                endIcon={<SaveIcon />}
                disabled={sendBtnDisabled}
              >
                Sauvegarder
              </LoadingButton>
            </span>
          </Tooltip>
        </Box>
        <Box sx={{ marginLeft: 1 }}>
          <Tooltip
            placement="top"
            title={
              receiverList?.length === 0 ? (
                'Veuillez ajouter un destinataire'
              ) : editor.getHTML().length <= 7 ? (
                'Veuillez écrire un message'
              ) : isTooLarge ? (
                <span style={{ fontSize: 20 }}>
                  Au dessus de la taille limite
                </span>
              ) : (
                ''
              )
            }
          >
            <span>
              <LoadingButton
                isLoading={loadingSend}
                size="small"
                variant="outlined"
                onClick={handleBtnSendMessage}
                color={isTooLarge ? 'error' : 'success'}
                endIcon={<SendIcon />}
                disabled={sendBtnDisabled}
              >
                Envoyer
              </LoadingButton>
            </span>
          </Tooltip>
        </Box>
      </Grid>
    </Grid>
  );
};

export default TemplateDisplay;
